// frontend/packages/business-console/src/pages/Scheduling/AppointmentTypesPanel.jsx
//
// Appointment-type (service) CRUD. Covers the backend validation fields:
// name, slug, durationMin, bufferBefore/AfterMin, minNoticeMin, horizonDays,
// maxPerDay, mode (instant|request), arrivalWindowMin, staffIds (multiselect),
// assignment (any|pick|fixed), priceCents, active. Price is entered in dollars
// and stored as cents. Config writes are businessadmin/manager server-side
// (F.10).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import {
  listAppointmentTypes,
  createAppointmentType,
  updateAppointmentType,
  deleteAppointmentType,
} from '../../services/schedulingService';
import { Surface, StatusPill, useTokens } from '../../components/primitives';

function serverMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

function slugify(name) {
  const base = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return /^[a-z]/.test(base) ? base : `svc-${base}`.slice(0, 64);
}

function emptyDraft() {
  return {
    name: '',
    slug: '',
    durationMin: 30,
    bufferBeforeMin: 0,
    bufferAfterMin: 0,
    minNoticeMin: 0,
    horizonDays: 30,
    maxPerDay: '',
    mode: 'request',
    arrivalWindowMin: '',
    staffIds: [],
    assignment: 'any',
    priceDollars: '',
    active: true,
  };
}

function typeToDraft(ty) {
  return {
    _id: ty._id,
    name: ty.name || '',
    slug: ty.slug || '',
    durationMin: ty.durationMin ?? 30,
    bufferBeforeMin: ty.bufferBeforeMin ?? 0,
    bufferAfterMin: ty.bufferAfterMin ?? 0,
    minNoticeMin: ty.minNoticeMin ?? 0,
    horizonDays: ty.horizonDays ?? 30,
    maxPerDay: ty.maxPerDay ?? '',
    mode: ty.mode === 'instant' ? 'instant' : 'request',
    arrivalWindowMin: ty.arrivalWindowMin ?? '',
    staffIds: Array.isArray(ty.staffIds) ? ty.staffIds.map(String) : [],
    assignment: ['any', 'pick', 'fixed'].includes(ty.assignment)
      ? ty.assignment
      : 'any',
    priceDollars:
      ty.priceCents == null ? '' : (Number(ty.priceCents) / 100).toString(),
    active: ty.active !== false,
  };
}

// Coerce an optional integer text field to a number, or undefined when blank.
function optInt(value) {
  if (value === '' || value == null) return undefined;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? undefined : n;
}

export default function AppointmentTypesPanel({ staff }) {
  const t = useTokens();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);

  const staffById = new Map((staff || []).map((m) => [String(m._id), m.name]));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listAppointmentTypes();
      setTypes(rows);
    } catch (err) {
      setError(serverMessage(err, "Couldn't load services."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const save = async () => {
    if (!draft || busy) return;
    if (!draft.name.trim()) {
      setFormError('Enter a service name.');
      return;
    }
    const priceDollars = draft.priceDollars;
    let priceCents;
    if (priceDollars !== '' && priceDollars != null) {
      const n = Number(priceDollars);
      if (Number.isNaN(n) || n < 0) {
        setFormError('Enter a valid price.');
        return;
      }
      priceCents = Math.round(n * 100);
    }

    const payload = {
      name: draft.name.trim(),
      slug: (draft.slug.trim() || slugify(draft.name)).toLowerCase(),
      durationMin: optInt(draft.durationMin) ?? 30,
      bufferBeforeMin: optInt(draft.bufferBeforeMin) ?? 0,
      bufferAfterMin: optInt(draft.bufferAfterMin) ?? 0,
      minNoticeMin: optInt(draft.minNoticeMin) ?? 0,
      horizonDays: optInt(draft.horizonDays) ?? 30,
      mode: draft.mode,
      assignment: draft.assignment,
      staffIds: draft.staffIds,
      active: draft.active,
      maxPerDay: optInt(draft.maxPerDay) ?? null,
      arrivalWindowMin: optInt(draft.arrivalWindowMin) ?? null,
      priceCents: priceCents == null ? null : priceCents,
    };

    setBusy(true);
    setFormError(null);
    try {
      if (draft._id) await updateAppointmentType(draft._id, payload);
      else await createAppointmentType(payload);
      setDraft(null);
      load();
    } catch (err) {
      setFormError(serverMessage(err, "Couldn't save this service."));
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!deleteTarget || busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAppointmentType(deleteTarget._id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(serverMessage(err, "Couldn't remove this service."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Services
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={() => setDraft(emptyDraft())}
        >
          Add service
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : types.length === 0 ? (
        <Typography variant="body2" sx={{ color: t.muted }}>
          Add the services customers can book, each with a duration and booking
          mode.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {types.map((ty) => (
            <Box
              key={ty._id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {ty.name}
                  </Typography>
                  <StatusPill
                    size="sm"
                    kind={ty.mode === 'instant' ? 'success' : 'warning'}
                    label={ty.mode === 'instant' ? 'Instant' : 'Request'}
                  />
                  {ty.active === false ? (
                    <Chip size="small" label="Inactive" variant="outlined" />
                  ) : null}
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {ty.durationMin} min
                  {ty.priceCents != null
                    ? ` . $${(ty.priceCents / 100).toFixed(2)}`
                    : ''}
                  {Array.isArray(ty.staffIds) && ty.staffIds.length
                    ? ` . ${ty.staffIds.length} staff`
                    : ''}
                </Typography>
              </Box>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  aria-label={`Edit ${ty.name}`}
                  onClick={() => setDraft(typeToDraft(ty))}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  aria-label={`Remove ${ty.name}`}
                  onClick={() => setDeleteTarget(ty)}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={Boolean(draft)}
        onClose={() => (busy ? null : setDraft(null))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {draft?._id ? 'Edit service' : 'Add service'}
        </DialogTitle>
        <DialogContent>
          {formError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          ) : null}
          {draft ? (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Name"
                  size="small"
                  fullWidth
                  value={draft.name}
                  onChange={(e) => set('name', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Duration (min)"
                  size="small"
                  fullWidth
                  type="number"
                  value={draft.durationMin}
                  onChange={(e) => set('durationMin', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="URL slug"
                  size="small"
                  fullWidth
                  placeholder={slugify(draft.name)}
                  helperText="Leave blank to generate from the name."
                  value={draft.slug}
                  onChange={(e) => set('slug', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Price ($)"
                  size="small"
                  fullWidth
                  type="number"
                  value={draft.priceDollars}
                  onChange={(e) => set('priceDollars', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl size="small" fullWidth disabled={busy}>
                  <InputLabel id="mode-label">Mode</InputLabel>
                  <Select
                    labelId="mode-label"
                    label="Mode"
                    value={draft.mode}
                    onChange={(e) => set('mode', e.target.value)}
                  >
                    <MenuItem value="instant">Instant book</MenuItem>
                    <MenuItem value="request">Request to book</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="Buffer before (min)"
                  size="small"
                  fullWidth
                  type="number"
                  value={draft.bufferBeforeMin}
                  onChange={(e) => set('bufferBeforeMin', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Buffer after (min)"
                  size="small"
                  fullWidth
                  type="number"
                  value={draft.bufferAfterMin}
                  onChange={(e) => set('bufferAfterMin', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Min notice (min)"
                  size="small"
                  fullWidth
                  type="number"
                  value={draft.minNoticeMin}
                  onChange={(e) => set('minNoticeMin', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Horizon (days)"
                  size="small"
                  fullWidth
                  type="number"
                  value={draft.horizonDays}
                  onChange={(e) => set('horizonDays', e.target.value)}
                  disabled={busy}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="Max per day"
                  size="small"
                  fullWidth
                  type="number"
                  placeholder="No limit"
                  value={draft.maxPerDay}
                  onChange={(e) => set('maxPerDay', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Arrival window (min)"
                  size="small"
                  fullWidth
                  type="number"
                  placeholder="Exact time"
                  value={draft.arrivalWindowMin}
                  onChange={(e) => set('arrivalWindowMin', e.target.value)}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth disabled={busy}>
                  <InputLabel id="assignment-label">Assignment</InputLabel>
                  <Select
                    labelId="assignment-label"
                    label="Assignment"
                    value={draft.assignment}
                    onChange={(e) => set('assignment', e.target.value)}
                  >
                    <MenuItem value="any">Any available staff</MenuItem>
                    <MenuItem value="pick">Customer picks staff</MenuItem>
                    <MenuItem value="fixed">Fixed staff</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl size="small" fullWidth disabled={busy}>
                  <InputLabel id="staff-label">Staff who offer this</InputLabel>
                  <Select
                    labelId="staff-label"
                    label="Staff who offer this"
                    multiple
                    value={draft.staffIds}
                    onChange={(e) =>
                      set(
                        'staffIds',
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    renderValue={(selected) =>
                      selected
                        .map((id) => staffById.get(String(id)) || 'Staff')
                        .join(', ')
                    }
                  >
                    {(staff || []).map((m) => (
                      <MenuItem key={m._id} value={String(m._id)}>
                        <Checkbox
                          checked={draft.staffIds.includes(String(m._id))}
                        />
                        <ListItemText primary={m.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Switch
                    checked={draft.active}
                    onChange={(e) => set('active', e.target.checked)}
                    disabled={busy}
                  />
                  <Typography variant="body2">
                    Active (customers can book this service)
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDraft(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (busy ? null : setDeleteTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Remove {deleteTarget?.name || 'service'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Customers will no longer be able to book it. This can be restored by
            support.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={runDelete}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Surface>
  );
}
